import { useCallback, useReducer } from 'react';

import { UnifiedAccount } from '../../types/Account.interface';

// ✅ Phase 3: Simplified state - removed modal states (moved to useModalState)
interface ImportTransactionsState {
    // Account management
    newlyConnectedAccounts: UnifiedAccount[];
    selectedAccountForMenu: UnifiedAccount | undefined;
    selectedAccountForTransaction: UnifiedAccount | undefined;

    // Error states
    errors: Record<string, string>;
}

// ✅ Phase 3: Simplified actions - removed modal actions
type ImportTransactionsAction =
    | { type: 'SET_NEWLY_CONNECTED_ACCOUNTS'; payload: UnifiedAccount[] }
    | { type: 'ADD_NEWLY_CONNECTED_ACCOUNT'; payload: UnifiedAccount }
    | { type: 'ADD_MULTIPLE_NEWLY_CONNECTED_ACCOUNTS'; payload: UnifiedAccount[] }
    | { type: 'SET_SELECTED_ACCOUNT_FOR_MENU'; payload: UnifiedAccount | undefined }
    | { type: 'SET_SELECTED_ACCOUNT_FOR_TRANSACTION'; payload: UnifiedAccount | undefined }
    | { type: 'SET_OPERATION_STATE'; payload: { operation: string; state: unknown } }
    | { type: 'SET_ERROR'; payload: { operation: string; error: string } }
    | { type: 'CLEAR_ERROR'; payload: string }
    | { type: 'RESET_STATE' };

const initialState: ImportTransactionsState = {
    newlyConnectedAccounts: [],
    selectedAccountForMenu: undefined,
    selectedAccountForTransaction: undefined,
    errors: {},
};

function importTransactionsReducer(
    state: ImportTransactionsState,
    action: ImportTransactionsAction
): ImportTransactionsState {
    switch (action.type) {
        case 'SET_NEWLY_CONNECTED_ACCOUNTS':
            return { ...state, newlyConnectedAccounts: action.payload };

        case 'ADD_NEWLY_CONNECTED_ACCOUNT':
            return {
                ...state,
                newlyConnectedAccounts: [...state.newlyConnectedAccounts, action.payload],
            };

        case 'ADD_MULTIPLE_NEWLY_CONNECTED_ACCOUNTS':
            return {
                ...state,
                newlyConnectedAccounts: [...state.newlyConnectedAccounts, ...action.payload],
            };

        case 'SET_SELECTED_ACCOUNT_FOR_MENU':
            return { ...state, selectedAccountForMenu: action.payload };

        case 'SET_SELECTED_ACCOUNT_FOR_TRANSACTION':
            return { ...state, selectedAccountForTransaction: action.payload };

        case 'SET_OPERATION_STATE':
            return {
                ...state,
                [action.payload.operation]: action.payload.state,
            };

        case 'SET_ERROR':
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.operation]: action.payload.error,
                },
            };

        case 'CLEAR_ERROR': {
            const { [action.payload]: removedError, ...remainingErrors } = state.errors;
            return { ...state, errors: remainingErrors };
        }

        case 'RESET_STATE':
            return initialState;

        default:
            return state;
    }
}

function useImportTransactionsState() {
    const [state, dispatch] = useReducer(importTransactionsReducer, initialState);

    // Account management actions
    const setNewlyConnectedAccounts = useCallback((accounts: UnifiedAccount[]) => {
        dispatch({ type: 'SET_NEWLY_CONNECTED_ACCOUNTS', payload: accounts });
    }, []);

    const addNewlyConnectedAccount = useCallback((account: UnifiedAccount) => {
        dispatch({ type: 'ADD_NEWLY_CONNECTED_ACCOUNT', payload: account });
    }, []);

    const addMultipleNewlyConnectedAccounts = useCallback((accounts: UnifiedAccount[]) => {
        dispatch({ type: 'ADD_MULTIPLE_NEWLY_CONNECTED_ACCOUNTS', payload: accounts });
    }, []);

    const setSelectedAccountForMenu = useCallback((account: UnifiedAccount | undefined) => {
        dispatch({ type: 'SET_SELECTED_ACCOUNT_FOR_MENU', payload: account });
    }, []);

    const setSelectedAccountForTransaction = useCallback((account: UnifiedAccount | undefined) => {
        dispatch({ type: 'SET_SELECTED_ACCOUNT_FOR_TRANSACTION', payload: account });
    }, []);

    // Operation actions
    const setOperationState = useCallback((operation: string, operationState: unknown) => {
        dispatch({ type: 'SET_OPERATION_STATE', payload: { operation, state: operationState } });
    }, []);

    // Error actions
    const setError = useCallback((operation: string, error: string) => {
        dispatch({ type: 'SET_ERROR', payload: { operation, error } });
    }, []);

    const clearError = useCallback((operation: string) => {
        dispatch({ type: 'CLEAR_ERROR', payload: operation });
    }, []);

    return {
        // State
        state,

        // Account actions
        setNewlyConnectedAccounts,
        addNewlyConnectedAccount,
        addMultipleNewlyConnectedAccounts,
        setSelectedAccountForMenu,
        setSelectedAccountForTransaction,

        // Operation actions
        setOperationState,

        // Error actions
        setError,
        clearError,
    };
}

export default useImportTransactionsState;
